import styled from '@emotion/styled';

/**
 * ScenarioOverviewPage — Page 7 of the Kuutio print report.
 *
 * "3. SKENAARIOT JA TULEVAISUUSKUVAT" — left body text, right 2x2 colored circles.
 * All positions from IDML Spread_ud5.xml via idml-to-css.mjs.
 *
 * Ovals (circles):
 *   ua2e/Voikukka: left:495.9pt (but on p7 different spread) — yellow DEMOKRATIA
 *   ua2b/Mansikkasmoothie: pink VÄESTÖ
 *   Kerkkä: green TEKNOLOGIA
 *   Tellus: blue ILMASTO
 *
 * Page 7 uses Spread_ud5 with 4 ovals + text labels + body text + callout box.
 */
export default function ScenarioOverviewPage({
  instanceNumber = '3',
  topic = 'Kansainvälisten opiskelijoiden työllistyminen Suomeen',
}) {
  return (
    <Page className="page">
      <TitleFrame>
        <Title>3. SKENAARIOT JA TULEVAISUUSKUVAT</Title>
      </TitleFrame>
      <YellowRule />

      <BodyFrame>
        <Body>
          Kuutio-työskentelyn ideana on tarkastella valittua aihetta eri
          näkökulmista. Kuution vakioskenaariot ovat demokratia, väes­tö,
          teknologia sekä ilmasto. Tavoitteena on löytää syy-seuraus­suhteita
          sekä moniulotteisia vipupisteitä.
        </Body>
        <Body>
          Työskentely mahdollistaa itselle tutun aiheen tarkastelun
          uudesta näkökulmasta. Tulevaisuuskuvien muuttujat ohjaavat
          työskentelyä kohti konkretiaa.
        </Body>
        <Body>
          Osallistujat muodostivat nopeana harjoituksena myös jokaiseesta
          skenaariosta neljännen tulevaisuuskuvan. Fasilitaattorien
          muistiinpanojen pohjalta tiivistetyt tulevaisuuskuvatarinat on
          tehty Googlen Gemini Pro -tekoälyllä.
        </Body>
        <Body>
          Seuraavaksi tehtävänä oli valita kustakin vakioskenaariosta yksi
          tulevaisuuskuva vaikutuskarttatyöskentelyn pohjalle. Ryhmän
          perustelut tallennettiin äänitiedostona osaksi työskentelyn
          dokumentaatiota.
        </Body>
      </BodyFrame>

      {/* Callout box at bottom-left */}
      <CalloutBox>
        <CalloutBorder />
        <CalloutContent>
          <CalloutText>
            <em>Löydät vakioskenaarioiden taustaraportit{'\n'}<a href="https://sitra.fi/kuutio" target="_blank" rel="noopener noreferrer"><strong>sitra.fi/kuutio</strong></a> -sivulta</em>
          </CalloutText>
        </CalloutContent>
      </CalloutBox>

      {/* 2x2 scenario circles (98.2pt diameter) with labels centered below
           Circle positions derived from IDML caption text centers:
           Row 1: Demokratia caption centered ~543pt, Väestö ~747pt
           Row 2: Teknologia caption centered ~529pt, Ilmasto ~739pt
           Circles centered above captions: circle left = caption center - 49.1pt */}

      {/* Row 1: Demokratia + Väestö */}
      <ScenarioCircle $color="#FBE300" $left="485pt" $top="91.2pt" $size="98.2pt">
        <CircleLabel>DEMOKRATIA</CircleLabel>
      </ScenarioCircle>
      <CircleCaption style={{ left: '439.8pt', top: '197.3pt', width: '190.4pt' }}>
        Kolme demokratian{'\n'}tulevaisuuskuvaa
      </CircleCaption>

      <ScenarioCircle $color="#FCCDD3" $left="698pt" $top="91.2pt" $size="98.2pt">
        <CircleLabel>VÄESTÖ</CircleLabel>
      </ScenarioCircle>
      <CircleCaption style={{ left: '660.1pt', top: '197.3pt', width: '158.7pt' }}>
        Kolme väestön{'\n'}tulevaisuuskuvaa
      </CircleCaption>

      {/* Row 2: Teknologia + Ilmasto */}
      <ScenarioCircle $color="#6BBF23" $left="480pt" $top="260pt" $size="98.2pt">
        <CircleLabel>TEKNOLOGIA</CircleLabel>
      </ScenarioCircle>
      <CircleCaption style={{ left: '436.4pt', top: '365.6pt', width: '184.6pt' }}>
        Kolme teknologian{'\n'}tulevaisuuskuvaa
      </CircleCaption>

      <ScenarioCircle $color="#7CCDE4" $left="690pt" $top="260pt" $size="98.2pt">
        <CircleLabel>ILMASTO</CircleLabel>
      </ScenarioCircle>
      <CircleCaption style={{ left: '660.1pt', top: '365.6pt', width: '158.7pt' }}>
        Kolme ilmaston{'\n'}tulevaisuuskuvaa
      </CircleCaption>

      <PageFooter>
        <FooterLeft><strong>Kuutio #{instanceNumber}:</strong> {topic}</FooterLeft>
        <FooterRight>7</FooterRight>
      </PageFooter>
    </Page>
  );
}

const Page = styled.div`
  width: 297mm;
  min-height: 210mm;
  background: #fff;
  color: #000;
  position: relative;
  font-family: 'Rational', sans-serif;
  box-sizing: border-box;
  overflow: hidden;
  @media print { break-after: page; }
`;

const TitleFrame = styled.div`
  position: absolute;
  left: 40.0pt;
  top: 30.0pt;
  width: 479.2pt;
`;

const Title = styled.h1`
  font-size: 22pt;
  font-weight: 700;
  font-style: italic;
  text-transform: uppercase;
  margin: 0;
`;

const YellowRule = styled.div`
  position: absolute;
  left: 42.5pt;
  top: 58pt;
  width: calc(297mm - 42.5pt - 28.3pt);
  height: 3pt;
  background: #FBE300;
`;

const BodyFrame = styled.div`
  position: absolute;
  left: 42.5pt;
  top: 84.2pt;
  width: 335.9pt;
`;

const Body = styled.p`
  font-size: 11pt;
  font-weight: 400;
  line-height: 13pt;
  margin: 0 0 10pt 0;
`;

const CalloutBox = styled.div`
  position: absolute;
  left: 42.5pt;
  top: 383.1pt;
  width: 336.0pt;
  height: 43.5pt;
  display: flex;
`;

const CalloutBorder = styled.div`
  width: 5pt;
  background: #7CCDE4;
  flex-shrink: 0;
`;

const CalloutContent = styled.div`
  background: rgba(124, 205, 228, 0.33);
  padding: 10pt 14pt;
  flex: 1;
`;

const CalloutText = styled.p`
  font-size: 10pt;
  line-height: 13pt;
  margin: 0;
  white-space: pre-line;
`;

const ScenarioCircle = styled.div`
  position: absolute;
  left: ${p => p.$left};
  top: ${p => p.$top};
  width: ${p => p.$size};
  height: ${p => p.$size};
  border-radius: 50%;
  background: ${p => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* IDML: Bold Italic 20.8pt, AllCaps — uses RationalText-BoldItalic.otf */
const CircleLabel = styled.span`
  font-size: 21pt;
  font-weight: 700;
  font-style: italic;
  text-align: center;
  text-transform: uppercase;
  color: #000;
`;

const CircleCaption = styled.div`
  position: absolute;
  font-size: 11pt;
  font-weight: 400;
  line-height: 15pt;
  text-align: center;
  white-space: pre-line;
`;

const PageFooter = styled.div`
  position: absolute;
  bottom: 14pt;
  left: 42.5pt;
  right: 28.3pt;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 0.5pt solid rgba(0, 0, 0, 0.3);
  padding-top: 6pt;
`;
const FooterLeft = styled.span` font-size: 8pt; font-style: italic; `;
const FooterRight = styled.span` font-size: 10pt; `;
